import React, { useState, useEffect, memo } from 'react';
import PropTypes from 'prop-types';
import TicketModal from './TicketModal';
import './TicketCard.css';

const TicketCard = memo(({ ticket, draggable = false, onDragStart, isNewDrop = false }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [animate, setAnimate] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleDragStart = (e) => {
    if (onDragStart) {
      onDragStart(e);
      setIsDragging(true);
    }
  };

  const handleDragEnd = () => {
    setIsDragging(false);
  };

  const handleCardClick = (e) => {
    if (!isDragging) {
      setIsModalOpen(true);
      // Prevent body scroll when modal is open
      document.body.classList.add('modal-open');
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    // Re-enable body scroll when modal is closed
    document.body.classList.remove('modal-open');
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      document.body.classList.remove('modal-open');
    };
  }, []);

  const getStatusClass = (status) => {
    if (!status) return '';
    switch (status.toLowerCase()) {
      case 'done': return 'status-done';
      case 'in progress': return 'status-in-progress';
      case 'blocked': return 'status-blocked';
      case 'to do':
      case 'todo': return 'status-todo';
      case 'review': return 'status-review';
      case 'selected for development': return 'status-selected-for-development';
      case 'waiting for review': return 'status-waiting-for-review';
      case 'rejected': return 'status-rejected';
      case 'canceled': return 'status-canceled';
      case 'new': return 'status-new';
      default: return 'status-todo';
    }
  };

  const getPriorityClass = (priority) => {
    if (!priority) return '';
    switch (priority.toLowerCase()) {
      case 'high': return 'priority-high';
      case 'medium': return 'priority-medium';
      case 'low': return 'priority-low';
      default: return '';
    }
  };

  const getCategoryBadgeStyle = (category) => {
    if (!category) return null;
    
    // Generate a consistent color based on category name
    const colors = [
      '#6f42c1', '#17a2b8', '#fd7e14', '#e83e8c', '#20c997', '#6610f2'
    ];
    const hash = category.split('').reduce((a, b) => {
      a = ((a << 5) - a) + b.charCodeAt(0);
      return a & a;
    }, 0);
    const backgroundColor = colors[Math.abs(hash) % colors.length];
    
    return {
      backgroundColor,
      color: 'white',
      padding: '2px 8px',
      borderRadius: '10px',
      fontSize: '0.7rem',
      fontWeight: '600',
      display: 'inline-block',
      marginLeft: '4px'
    };
  };

  const getEffortBadgeStyle = (effort) => {
    if (!effort || effort <= 0) return null;
    
    // Color based on effort level (in days)
    let backgroundColor = '#28a745'; // Green for low effort
    let color = 'white';
    
    if (effort > 5) {
      backgroundColor = '#dc3545'; // Red for high effort (>5 days)
    } else if (effort > 2) {
      backgroundColor = '#ffc107'; // Yellow for medium effort (2-5 days)
      color = '#333'; // Dark text for yellow background
    }
    
    return {
      backgroundColor,
      color,
      padding: '2px 8px',
      borderRadius: '10px',
      fontSize: '0.7rem',
      fontWeight: '600',
      display: 'inline-block',
      marginLeft: '4px'
    };
  };

  useEffect(() => {
    if (isNewDrop) {
      setAnimate(true);
      const timer = setTimeout(() => {
        setAnimate(false);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [isNewDrop]);

  return (
    <>
      <div
        id={`ticket-${ticket.issue_key}`}
        className={`ticket-card ${isDragging ? 'dragging' : ''} ${animate ? 'animate-drop' : ''}`}
        draggable={draggable}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onClick={handleCardClick}
        style={{ cursor: draggable ? 'grab' : 'pointer' }}
      >
        <div className="ticket-key">{ticket.issue_key}</div>
        <div className="ticket-summary">{ticket.summary || 'No summary available'}</div>
        
        {/* Main badges row */}
        <div style={{ display: 'flex', gap: '4px', marginTop: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
          <span className={`status-badge ${getStatusClass(ticket.status)}`}>
            {ticket.status}
          </span>
          {ticket.priority && (
            <span className={`priority-badge ${getPriorityClass(ticket.priority)}`}>
              {ticket.priority}
            </span>
          )}
        </div>

        {/* Secondary badges row for new fields */}
        {(ticket.category || ticket.initial_effort_estimations) && (
          <div style={{ display: 'flex', gap: '4px', marginTop: '4px', flexWrap: 'wrap', alignItems: 'center' }}>
            {ticket.category && (
              <span style={getCategoryBadgeStyle(ticket.category)}>
                📂 {ticket.category}
              </span>
            )}
            {ticket.initial_effort_estimations && (
              <span style={getEffortBadgeStyle(ticket.initial_effort_estimations)}>
                ⚡ {ticket.initial_effort_estimations} {ticket.initial_effort_estimations === 1 ? 'day' : 'days'}
              </span>
            )}
          </div>
        )}
      </div>

      <TicketModal 
        isOpen={isModalOpen} 
        onClose={handleCloseModal} 
        ticket={ticket}
      />
    </>
  );
});

TicketCard.displayName = 'TicketCard';

TicketCard.propTypes = {
  ticket: PropTypes.shape({
    issue_key: PropTypes.string,
    summary: PropTypes.string,
    description: PropTypes.string,
    status: PropTypes.string,
    priority: PropTypes.string,
    category: PropTypes.string, // ✅ NEW FIELD
    initial_effort_estimations: PropTypes.number, // ✅ NEW FIELD (FLOAT)
    sprint: PropTypes.string
  }).isRequired,
  draggable: PropTypes.bool,
  onDragStart: PropTypes.func,
  isNewDrop: PropTypes.bool
};

export default TicketCard;